import { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { Link, useLocation } from 'react-router-dom';
import { KaneaLogo } from '../../components/Common/KaneaLogo';

// hooks
import useFormEvents from '../../hooks/useFormEvents';
import usePageTitle from '../../hooks/usePageTitle';

// styles
import '../../styles/auth.css';
import AuthBrandCanvas from '../../components/Auth/AuthBrandCanvas';


// interfaces
interface IFormProps {
  email: string;
  phone: string;
  password: string;
  password1: string;
  name: string;
  lastname: string;
  country: string;
  promo_code: string;
  agreeToPolicies1: boolean;
  agreeToPersonalUse: boolean;
}

const SignupScreen: React.FC = () => {
  usePageTitle('Inscription sur KaneaBourse');
  const { onlyNumbers, onlyEmail } = useFormEvents();
  const location = useLocation();

  const [formValues, setFormValues] = useState<IFormProps>({
    email: '',
    phone: '',
    password: '',
    password1: '',
    name: '',
    lastname: '',
    country: '',
    promo_code: '',
    agreeToPolicies1: false,
    agreeToPersonalUse: false,
  });

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [signupSuccess, setSignupSuccess] = useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [resendStatus, setResendStatus] = useState<string>('');

  // Handle pre-filling promo code from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref') || params.get('promo') || params.get('promo_code');
    if (refCode) {
      setFormValues(prev => ({ ...prev, promo_code: refCode }));
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    // Le téléphone n'accepte que des chiffres et des espaces, y compris au collage (onKeyDown ne le couvrait pas).
    const propre = name === 'phone' ? value.replace(/[^\d ]/g, '') : value;
    setFormValues({ ...formValues, [name]: propre });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = e.target;
    setFormValues({ ...formValues, [name]: checked });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.email)) {
      setError('Veuillez entrer une adresse email valide.');
      return;
    }
    if (formValues.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (formValues.password !== formValues.password1) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!formValues.name.trim() || !formValues.lastname.trim()) {
      setError('Veuillez entrer votre prénom et nom.');
      return;
    }
    if (!formValues.country) {
      setError('Veuillez sélectionner un indicatif pays.');
      return;
    }
    if (!formValues.phone || formValues.phone.trim().length < 8) {
      setError('Veuillez entrer un numéro de téléphone valide.');
      return;
    }
    if (!formValues.agreeToPolicies1) {
      setError('Veuillez accepter les conditions d\'utilisation.');
      return;
    }
    if (!formValues.agreeToPersonalUse) {
      setError('Veuillez certifier que ce compte est utilisé à titre personnel (investisseur particulier B2C).');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formValues.email,
          password: formValues.password,
          full_name: `${formValues.name} ${formValues.lastname}`,
          country_code: formValues.country,
          phone_number: formValues.phone,
          promo_code: formValues.promo_code,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Échec de l\'inscription');
      }

      setRegisteredEmail(formValues.email);
      setSignupSuccess(true);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Une erreur inattendue s\'est produite.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail }),
      });
      if (response.ok) {
        setError('');
        setResendStatus('Email renvoyé avec succès !');
        setTimeout(() => setResendStatus(''), 3000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /* ── Panneau gauche commun ── */
  const BrandPanel = () => (
    <div className="auth-brand-panel">
      {/* Animation interactive : réseau de particules + spotlight souris */}
      <AuthBrandCanvas />
      <div className="auth-brand-spotlight" />

      <div className="auth-brand-logo">
        <div className="auth-brand-logo-img-wrap">
          <KaneaLogo variant="mark" title={null} />
        </div>
        <span className="auth-brand-name">KaneaBourse</span>
        <div className="auth-brand-logo-dot" />
      </div>

      <div className="auth-brand-body">
        <h2>
          N'investissez plus sur des <em>rumeurs</em>
        </h2>
        <p>
          Rejoignez les investisseurs qui analysent les bilans officiels BRVM
          avec méthode — chiffres réels, 14 modèles de valorisation, copilote Nia.
        </p>
        <div className="auth-brand-stats">
          <div>
            <div className="auth-brand-stat-value">10s</div>
            <div className="auth-brand-stat-label">Pour décider</div>
          </div>
          <div>
            <div className="auth-brand-stat-value">48</div>
                <div className="auth-brand-stat-label">Sociétés suivies</div>
          </div>
          <div>
            <div className="auth-brand-stat-value">24/7</div>
            <div className="auth-brand-stat-label">Copilote Nia</div>
          </div>
        </div>
      </div>

      <div className="auth-brand-footer">
        © {new Date().getFullYear()} KaneaBourse · Données EOD BRVM
      </div>
    </div>
  );

  /* ── Vue succès (email envoyé) ── */
  if (signupSuccess) {
    return (
      <div className="auth-root">
        <div className="auth-panel">
          <BrandPanel />
          <div className="auth-form-panel">
            <div className="auth-form-inner">
              
              {/* ── Logo Mobile ── */}
              <div className="auth-mobile-logo">
                <KaneaLogo variant="mark" title={null} />
                <span className="auth-mobile-logo-text">KaneaBourse</span>
              </div>

              <div className="auth-success-card">
                <div className="auth-success-icon">📧</div>
                <h2>Vérifiez votre email</h2>
                <p>Un email d'activation a été envoyé à :</p>
                <div className="auth-email-chip">{registeredEmail}</div>
                <p style={{ fontSize: '0.83rem', color: '#6B7280' }}>
                  Cliquez sur le lien dans l'email pour activer votre compte.
                  <br />Le lien expire dans 24 heures.
                </p>
                <button
                  onClick={handleResendVerification}
                  className="auth-btn-secondary"
                  style={{ marginTop: 20 }}
                >
                  🔄 Renvoyer l'email
                </button>
                {resendStatus && (
                  <p className="auth-resend-status">{resendStatus}</p>
                )}
                <div className="auth-form-link" style={{ marginTop: 20 }}>
                  <Link to="/login">← Retour à la connexion</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Formulaire d'inscription ── */
  return (
    <div className="auth-root">
      <div className="auth-panel">
        <BrandPanel />

        <div className="auth-form-panel">
          <div className="auth-form-inner">

            {/* ── Logo Mobile ── */}
            <div className="auth-mobile-logo">
              <KaneaLogo variant="mark" title={null} />
              <span className="auth-mobile-logo-text">KaneaBourse</span>
            </div>

            {/* Header */}
            <div className="auth-form-header">
              <h1>Créer un compte</h1>
              <p>Accédez gratuitement aux outils d'analyse BRVM.</p>
            </div>

            {/* Erreur */}
            {error && (
              <div className="auth-error">
                <span>⚠</span> {error}
              </div>
            )}

            <form noValidate onSubmit={handleSubmit}>

              {/* Email */}
              <div className="auth-field">
                <label htmlFor="email">Adresse email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  onKeyDown={onlyEmail}
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                />
              </div>

              {/* Prénom + Nom */}
              <div className="auth-field-row">
                <div className="auth-field">
                  <label htmlFor="name">Prénom</label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    placeholder="Kofi"
                    autoComplete="given-name"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="lastname">Nom</label>
                  <input
                    id="lastname"
                    type="text"
                    name="lastname"
                    value={formValues.lastname}
                    onChange={handleChange}
                    placeholder="Mensah"
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Mot de passe */}
              <div className="auth-field">
                <label htmlFor="password">Mot de passe</label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={formValues.password}
                  onChange={handleChange}
                  placeholder="Au moins 8 caractères"
                  autoComplete="new-password"
                />
              </div>

              {/* Confirmation */}
              <div className="auth-field">
                <label htmlFor="password1">Confirmer le mot de passe</label>
                <input
                  id="password1"
                  type="password"
                  name="password1"
                  value={formValues.password1}
                  onChange={handleChange}
                  placeholder="Répétez votre mot de passe"
                  autoComplete="new-password"
                />
              </div>

              {/* Téléphone */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--auth-text, #1E1B2E)', marginBottom: 7 }}>
                  Numéro de téléphone
                </label>
                <div className="auth-phone-row" style={{ marginBottom: 0 }}>
                  <div className="auth-phone-select">
                    <select
                      name="country"
                      id="country"
                      aria-label="Indicatif du pays"
                      value={formValues.country}
                      onChange={handleChange}
                    >
                      <option value="">Indicatif</option>
                      <option value="+225">🇨🇮 CI (+225)</option>
                      <option value="+221">🇸🇳 SN (+221)</option>
                      <option value="+226">🇧🇫 BF (+226)</option>
                      <option value="+228">🇹🇬 TG (+228)</option>
                      <option value="+229">🇧🇯 BJ (+229)</option>
                      <option value="+223">🇲🇱 ML (+223)</option>
                      <option value="+227">🇳🇪 NE (+227)</option>
                      <option value="+245">🇬🇼 GW (+245)</option>
                      <option value="+33">🇫🇷 FR (+33)</option>
                    </select>
                  </div>
                  <div className="auth-phone-input">
                    <input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      aria-label="Numéro de téléphone"
                      name="phone"
                      value={formValues.phone}
                      onChange={handleChange}
                      onKeyDown={onlyNumbers}
                      placeholder="XX XX XX XX"
                      maxLength={15}
                    />
                  </div>
                </div>
              </div>

              {/* Code parrainage */}
              <div className="auth-field">
                <label htmlFor="promo_code">
                  Code de parrainage
                  <span className="auth-field-optional">(optionnel)</span>
                </label>
                <input
                  id="promo_code"
                  type="text"
                  name="promo_code"
                  value={formValues.promo_code}
                  onChange={handleChange}
                  placeholder="Ex: KANEA2024"
                />
              </div>

              {/* CGU */}
              <div className="auth-checkbox-row">
                <input
                  type="checkbox"
                  id="agreeToPolicies1"
                  name="agreeToPolicies1"
                  checked={formValues.agreeToPolicies1}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="agreeToPolicies1">
                  J'ai lu et j'accepte la{' '}
                  <Link to="/confidentialite" target="_blank">Politique de confidentialité</Link>
                  {' '}et les{' '}
                  <Link to="/terms" target="_blank">Conditions d'utilisation</Link>.
                </label>
              </div>

              {/* Certification usage personnel B2C */}
              <div className="auth-checkbox-row" style={{ marginTop: 12 }}>
                <input
                  type="checkbox"
                  id="agreeToPersonalUse"
                  name="agreeToPersonalUse"
                  checked={formValues.agreeToPersonalUse}
                  onChange={handleCheckboxChange}
                />
                <label htmlFor="agreeToPersonalUse">
                  Je certifie que ce compte est destiné à un <strong>usage strictement personnel (investisseur particulier B2C)</strong> et non à un usage professionnel, institutionnel (SGI, banque, fond) ou d'académie/formation.
                </label>
              </div>

              {/* Note pour professionnels & institutionnels */}
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#F8F7FF', borderRadius: 8, border: '1px solid rgba(108, 63, 196, 0.2)', fontSize: '0.78rem', color: '#4B5563', lineHeight: '1.4' }}>
                💼 <strong>Vous êtes une SGI, Entreprise, Institutionnel ou Formateur ?</strong><br/>
                Ces offres sont réservées aux particuliers. <a href="mailto:contact@kaneabourse.com?subject=Demande%20Offre%20B2B%20/%20Institutionnel" style={{ color: '#6C3FC4', fontWeight: 700, textDecoration: 'underline' }}>Contactez notre équipe B2B pour une licence dédiée</a>.
              </div>

              {/* Bouton submit */}
              <button
                type="submit"
                className="auth-btn-primary"
                disabled={loading}
              >
                {loading ? 'Création du compte…' : 'Créer mon compte gratuitement →'}
              </button>

              {/* Lien connexion */}
              <div className="auth-form-link">
                Déjà un compte ?{' '}
                <Link to="/login">Se connecter</Link>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupScreen;
